import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { theme, forms } from '../styles/index.js';
import { showToast } from './toast-manager.js';
import { formatLabel } from '../utils/format-label.js';

interface ConfigParam {
  name: string;
  envVar: string;
  type: string;
  isOptional: boolean;
  hasDefault: boolean;
  defaultValue?: any;
  /** Server-side flag: name matches the secret regex. */
  isSecret?: boolean;
  /**
   * Echo of the currently-set value (or '***' for secret-named fields,
   * or null when the env var is empty). Used to render placeholder text
   * for already-configured photons.
   */
  currentValue?: string | null;
  /** Per-parameter JSDoc help text from the source. */
  description?: string;
}

interface UnconfiguredPhoton {
  name: string;
  configured?: boolean;
  requiredParams: ConfigParam[];
  errorMessage?: string;
  errorReason?: 'missing-config' | 'load-error' | 'missing-auth';
}

@customElement('photon-config')
export class PhotonConfig extends LitElement {
  static styles = [
    theme,
    forms,
    css`
      :host {
        display: block;
      }

      .config-header {
        margin-bottom: var(--space-lg);
      }

      .config-title {
        font-size: var(--text-2xl);
        font-weight: 600;
        margin: 0 0 var(--space-sm) 0;
      }

      .config-description {
        color: var(--t-muted);
        font-size: var(--text-md);
      }

      .config-form {
        display: flex;
        flex-direction: column;
        gap: var(--space-md);
      }

      .form-group {
        margin-bottom: 0; /* parent .config-form uses gap */
        display: flex;
        flex-direction: column;
        gap: var(--space-xs);
      }

      .form-group label {
        display: flex;
        align-items: center;
        gap: var(--space-sm);
      }

      .form-group label .required {
        color: var(--color-error);
      }

      .form-group label .hint {
        font-size: var(--text-xs);
        color: var(--t-muted);
        font-family: var(--font-mono);
        background: var(--bg-glass);
        padding: 2px 6px;
        border-radius: var(--radius-xs);
      }

      .form-group input {
        padding: var(--space-sm) var(--space-md);
        font-size: var(--text-md);
      }

      /* Mask secret fields without type="password" to avoid browser credential autofill */
      .form-group input.secret-field {
        -webkit-text-security: disc;
      }

      .toggle-switch {
        display: flex;
        background: var(--bg-glass);
        border: 1px solid var(--border-glass);
        border-radius: var(--radius-sm);
        overflow: hidden;
        width: fit-content;
      }

      .toggle-btn {
        padding: var(--space-sm) var(--space-md);
        background: transparent;
        border: none;
        color: var(--t-muted);
        cursor: pointer;
        font-size: var(--text-md);
        transition: all 0.2s;
      }

      .toggle-btn.active {
        background: var(--accent-primary);
        color: white;
      }

      .toggle-btn:hover:not(.active) {
        background: var(--bg-glass-strong);
      }

      .submit-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-sm);
        padding: var(--space-md);
        background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary));
        border: none;
        color: white;
        font-weight: 600;
        font-size: var(--text-lg);
        border-radius: var(--radius-sm);
        cursor: pointer;
        margin-top: var(--space-md);
        transition: all 0.2s;
      }

      .submit-btn:hover {
        opacity: 0.9;
        transform: translateY(-1px);
      }

      .submit-btn:disabled {
        opacity: 0.5;
        cursor: not-allowed;
        transform: none;
      }

      .submit-btn svg {
        width: 18px;
        height: 18px;
      }

      .error-banner {
        background: hsla(0, 70%, 50%, 0.15);
        border: 1px solid hsla(0, 70%, 50%, 0.3);
        color: var(--color-error);
        padding: var(--space-sm) var(--space-md);
        border-radius: var(--radius-sm);
        font-size: var(--text-md);
        margin-bottom: var(--space-md);
      }

      /* ===== Responsive Design ===== */
      @media (max-width: 768px) {
        .form-group input {
          min-height: 44px;
          font-size: 16px; /* Prevent iOS zoom */
        }

        .submit-btn {
          min-height: 44px;
          width: 100%;
        }

        .toggle-switch {
          width: 100%;
        }

        .toggle-btn {
          flex: 1;
          min-height: 44px;
        }
      }

      @media (max-width: 480px) {
        .config-title {
          font-size: 1.25rem;
        }

        .form-group label {
          flex-direction: column;
          align-items: flex-start;
          gap: var(--space-xs);
        }

        .form-group label .hint {
          font-size: 0.7rem;
        }
      }
    `,
  ];

  @property({ type: Object })
  photon: UnconfiguredPhoton | null = null;

  @property({ type: String })
  mode: 'initial' | 'edit' = 'initial';

  @state()
  private _loading = false;

  @state()
  private _formData: Record<string, string> = {};

  /**
   * When true, the parent view (Settings → Setup tab) is providing the
   * page header; suppress the in-component title + description.
   */
  @property({ type: Boolean, attribute: 'embedded' })
  embedded = false;

  render() {
    if (!this.photon) return html``;

    if (this.photon.errorReason === 'missing-auth') {
      return this._renderAuthForm();
    }

    const params = this.photon.requiredParams || [];

    return html`
      ${!this.embedded
        ? html`
            <div class="config-header">
              <h2 class="config-title text-gradient">${this.photon.name}</h2>
              <p class="config-description">
                ${this.mode === 'edit'
                  ? 'Edit configuration values. Only changed fields will be updated.'
                  : this.photon.errorMessage || 'Configure this photon to enable its features'}
              </p>
            </div>
          `
        : ''}
      ${this.mode === 'initial' && this.photon.errorMessage
        ? html` <div class="error-banner">${this.photon.errorMessage}</div> `
        : ''}

      <div class="config-form" role="presentation">
        ${params.map((param) => this._renderField(param))}

        <button
          type="button"
          class="submit-btn"
          ?disabled=${this._loading}
          @click=${() => this._handleSubmit()}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path
              d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"
            />
          </svg>
          ${this._loading
            ? 'Saving...'
            : this.mode === 'edit'
              ? 'Save Changes'
              : 'Configure & Enable'}
        </button>
      </div>
    `;
  }

  private _renderAuthForm() {
    return html`
      <div class="config-header">
        <h2 class="config-title text-gradient">${this.photon.name}</h2>
        <p class="config-description">
          This photon requires authentication. Enter your Bearer token to continue.
        </p>
      </div>
      <div class="config-form" role="presentation">
        <div class="form-group">
          <label>
            Bearer Token
            <span class="required" aria-label="required">*</span>
          </label>
          <input
            class="form-input"
            type="password"
            placeholder="Enter token..."
            .value=${this._formData['__auth_token'] || ''}
            @input=${(e: InputEvent) => {
              this._formData = {
                ...this._formData,
                __auth_token: (e.target as HTMLInputElement).value,
              };
            }}
          />
        </div>
        <button
          type="button"
          class="submit-btn"
          ?disabled=${this._loading || !this._formData['__auth_token']}
          @click=${() => {
            this.dispatchEvent(
              new CustomEvent('configure', {
                detail: {
                  photon: this.photon.name,
                  config: { type: 'auth-token', token: this._formData['__auth_token'] },
                },
                bubbles: true,
                composed: true,
              })
            );
          }}
        >
          Connect
        </button>
      </div>
    `;
  }

  private _renderField(param: ConfigParam) {
    const isRequired = !param.isOptional && !param.hasDefault;
    // Trust the server-side flag; fall back to the local heuristic for
    // older payloads that don't carry it.
    const isSecret = param.isSecret ?? this._isSecretField(param.name);
    const isBoolean = param.type === 'boolean';
    const isNumber = param.type === 'number';
    const defaultValue = param.hasDefault ? String(param.defaultValue ?? '') : '';

    // For configured photons, prefer the live value as the field's
    // initial input. Secret fields receive the placeholder marker only.
    const seedValue = param.currentValue ?? defaultValue;

    return html`
      <div class="form-group">
        <label>
          ${formatLabel(param.name)}${isRequired ? html`<span class="required">*</span>` : ''}
          <span class="hint">${param.envVar}</span>
          ${isSecret
            ? html`<span class="hint" style="color: var(--accent-secondary);">secret</span>`
            : ''}
        </label>

        ${param.description
          ? html`<p
              style="margin: 0; color: var(--t-muted); font-size: var(--text-xs); line-height: 1.4;"
            >
              ${param.description}
            </p>`
          : ''}
        ${isBoolean
          ? this._renderToggle(param, seedValue)
          : isNumber
            ? this._renderNumberInput(param, seedValue, isRequired)
            : this._renderTextInput(param, seedValue, isRequired, isSecret, !!param.currentValue)}
      </div>
    `;
  }

  private _renderToggle(param: ConfigParam, defaultValue: string) {
    const isOn = defaultValue === 'true';
    return html`
      <div class="toggle-switch">
        <button
          type="button"
          class="toggle-btn ${!isOn ? 'active' : ''}"
          @click=${() => this._setToggle(param.envVar, false)}
        >
          Off
        </button>
        <button
          type="button"
          class="toggle-btn ${isOn ? 'active' : ''}"
          @click=${() => this._setToggle(param.envVar, true)}
        >
          On
        </button>
      </div>
    `;
  }

  private _renderNumberInput(param: ConfigParam, defaultValue: string, isRequired: boolean) {
    return html`
      <input
        type="number"
        id="photon-${param.envVar}"
        name="${param.envVar}"
        .value=${this._formData[param.envVar] ?? defaultValue}
        placeholder="Enter ${param.name}..."
        @input=${(e: Event) =>
          this._updateField(param.envVar, (e.target as HTMLInputElement).value)}
      />
    `;
  }

  private _renderTextInput(
    param: ConfigParam,
    seedValue: string,
    isRequired: boolean,
    isSecret: boolean,
    hasCurrentValue: boolean
  ) {
    // For secret fields the seed is just '***' so don't surface it as the
    // editable value — show it as placeholder so the field appears
    // pre-filled visually but submitting blank means "leave unchanged".
    const masked = isSecret && hasCurrentValue;
    const placeholder = masked
      ? '••••••• (configured — type to replace)'
      : `Enter ${param.name}...`;
    const value = masked
      ? (this._formData[param.envVar] ?? '')
      : (this._formData[param.envVar] ?? seedValue);
    return html`
      <input
        type="text"
        id="photon-${param.envVar}"
        name="${param.envVar}"
        class="${isSecret ? 'secret-field' : ''}"
        .value=${value}
        placeholder="${placeholder}"
        @input=${(e: Event) =>
          this._updateField(param.envVar, (e.target as HTMLInputElement).value)}
      />
    `;
  }

  private _isSecretField(name: string): boolean {
    const lower = name.toLowerCase();
    return (
      lower.includes('password') ||
      lower.includes('secret') ||
      lower.includes('key') ||
      lower.includes('token')
    );
  }

  private _setToggle(envVar: string, value: boolean) {
    this._formData = { ...this._formData, [envVar]: String(value) };
  }

  private _updateField(envVar: string, value: string) {
    this._formData = { ...this._formData, [envVar]: value };
  }

  private _handleSubmit() {
    if (!this.photon) return;

    // Collect form data
    const config: Record<string, string> = {};
    const params = this.photon.requiredParams || [];

    for (const param of params) {
      const value = this._formData[param.envVar];
      if (value) {
        config[param.envVar] = value;
      } else if (param.hasDefault) {
        config[param.envVar] = String(param.defaultValue ?? '');
      }
    }

    this._loading = true;
    showToast(`Configuring ${this.photon.name}...`, 'info');

    this.dispatchEvent(
      new CustomEvent('configure', {
        detail: {
          photon: this.photon.name,
          config,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  // Called from parent when configuration completes
  configurationComplete(success: boolean, message?: string) {
    this._loading = false;
    if (success) {
      showToast(`${this.photon?.name} configured successfully!`, 'success');
    } else {
      showToast(message || 'Configuration failed', 'error');
    }
  }
}
